import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import {
  isChatSpace,
  isCourseSpace,
  isMembersSpace,
} from "@/react/helpers/spaceHelpers";
import type { Space } from "@/react/types/Space";
import { ASPECT_RATIO } from "@circle-react/components/constants";
import { useCustomizeUIData } from "@circle-react/components/shared/CustomizeUI";
import { useMobileWebFlags } from "@circle-react/hooks/useMobileWebFlags";
import { Form } from "@circle-react-uikit/Form";
import { ImageInput } from "@circle-react-uikit/ImageInput";

export interface SpaceImagesFormProps {
  onChange: (value: boolean) => void;
  space: Space;
}

export const SpaceImagesForm = ({ space, onChange }: SpaceImagesFormProps) => {
  const { setIsSaveButtonDisabled } = useCustomizeUIData();

  const {
    formState: { dirtyFields },
    watch,
    setValue,
  } = useFormContext();
  const coverImageValue = watch("cover_image");
  const { isMobileWebNewSpaceCustomizationUIEnabled } = useMobileWebFlags();

  useEffect(() => {
    if (coverImageValue) setValue("cover_image_visible", true);
    else setValue("cover_image_visible", false);
  }, [coverImageValue, setValue]);

  const shouldShouldCoverImage = !(
    isChatSpace(space) ||
    isCourseSpace(space) ||
    isMembersSpace(space)
  );

  const getThumbnailDescription = () => {
    if (isCourseSpace(space)) {
      return t("spaces.form.edit.customize.course_thumbnail_image_description");
    }
    return t("spaces.form.edit.customize.thumbnail_image_description");
  };

  useEffect(() => {
    if (
      "cover_image_visible" in dirtyFields ||
      "thumbnail_image" in dirtyFields
    ) {
      onChange(true);
    }
  }, [dirtyFields.cover_image_visible, dirtyFields.thumbnail_image]);

  return (
    <div>
      {shouldShouldCoverImage && (
        <Form.Item
          inline={!isMobileWebNewSpaceCustomizationUIEnabled}
          hideBorder
          label={t("spaces.form.edit.customize.cover_image")}
          name="cover_image"
          description={t(
            "spaces.form.edit.customize.cover_image_description_v3",
          )}
          className="gap-x-6"
        >
          <ImageInput
            name="cover_image"
            aspectRatio={ASPECT_RATIO.COVER_V3}
            onProcessing={processing => setIsSaveButtonDisabled(!!processing)}
          />
        </Form.Item>
      )}
      {!isChatSpace(space) && !isMembersSpace(space) && (
        <Form.Item
          inline={!isMobileWebNewSpaceCustomizationUIEnabled}
          hideBorder
          label={t("spaces.form.edit.customize.thumbnail_image")}
          name="thumbnail_image"
          description={getThumbnailDescription()}
          className="gap-x-6"
        >
          <ImageInput
            name="thumbnail_image"
            aspectRatio={16 / 9}
            onProcessing={processing => setIsSaveButtonDisabled(!!processing)}
          />
        </Form.Item>
      )}
    </div>
  );
};
